// Communications Intelligence — small, pure helpers shared across screens.
// No app-wide imports; nothing here touches the live hub.

// ── Canonical RSVP / audience interpretation (SINGLE SOURCE OF TRUTH) ─────────
// Mirrors backend server.py RSVP_YES_TOKENS / RSVP_NO_TOKENS (lowercased). This
// is the ONE place the Communications Intelligence module classifies RSVP state
// and audience segments — every CI screen imports from here, so Pending /
// Attending / Declined / VIP mean exactly the same thing everywhere, and the
// same thing the backend means. Pending is always the RESIDUAL (neither Yes nor
// No), matching the backend's derived-pending rule. To add a new RSVP token,
// add it to server.py RSVP_*_TOKENS first, then mirror it here — never widen one
// side alone.
export const RSVP_YES = new Set(['yes', 'confirmed']);
export const RSVP_NO = new Set(['no', 'declined']);

function rsvpNorm(g) {
  // Accept a raw status string OR a guest object ({rsvp_status} || {status}).
  if (g && typeof g === 'object') return String(g.rsvp_status ?? g.status ?? '').trim().toLowerCase();
  return String(g ?? '').trim().toLowerCase();
}
export function rsvpIsYes(status) { return RSVP_YES.has(rsvpNorm(status)); }
export function rsvpIsNo(status) { return RSVP_NO.has(rsvpNorm(status)); }
// Pending = anyone not explicitly Yes or No (blank, "pending", "invited", any
// unknown token) — the residual bucket, identical to the backend definition.
export function rsvpIsPending(g) { const s = rsvpNorm(g); return !RSVP_YES.has(s) && !RSVP_NO.has(s); }
export function isAttending(g) { return rsvpIsYes(g); }
export function isDeclined(g) { return rsvpIsNo(g); }
export function isVip(g) { return !!(g && g.is_vip); }

// The canonical audience segments. VIP is orthogonal (a guest can be VIP and
// also attending/pending); the segment filter treats it as its own view.
export const SEGMENTS = [
  { key: 'all', label: 'All Guests' },
  { key: 'pending', label: 'Pending RSVP' },
  { key: 'attending', label: 'Attending' },
  { key: 'heads', label: 'Primary' },   // = is_primary, the party's designated contact (host standardized on 'Primary', 2026-07-16)
  { key: 'vip', label: 'VIP' },
];
export function matchesSegment(g, seg) {
  if (seg === 'all') return true;
  if (seg === 'vip') return isVip(g);
  if (seg === 'attending') return isAttending(g);
  if (seg === 'pending') return rsvpIsPending(g);
  if (seg === 'heads') return !!g.is_primary;   // one designated head per party (host 2026-07-16)
  return true;
}

export function initials(name) {
  const parts = String(name || '').trim().split(/[\s&]+/).filter(Boolean);
  if (!parts.length) return 'G';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

// Forgiving relative time. Returns "" when unparseable.
export function relativeTime(value) {
  if (!value) return '';
  let dt;
  try {
    const s = String(value).endsWith('Z') ? String(value) : value;
    dt = new Date(s);
    if (Number.isNaN(dt.getTime())) return '';
  } catch {
    return '';
  }
  const diff = Date.now() - dt.getTime();
  const min = Math.round(diff / 60000);
  if (min < 1) return 'just now';
  if (min < 60) return `${min}m ago`;
  const hr = Math.round(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const day = Math.round(hr / 24);
  if (day < 7) return `${day}d ago`;
  return dt.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

export function shortDate(value) {
  if (!value) return '—';
  try {
    const dt = new Date(value);
    if (Number.isNaN(dt.getTime())) return String(value);
    return dt.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
  } catch {
    return String(value);
  }
}

export function shortDateTime(value) {
  if (!value) return '—';
  try {
    const dt = new Date(value);
    if (Number.isNaN(dt.getTime())) return String(value);
    return dt.toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
  } catch {
    return String(value);
  }
}

// Title-case a snake/lower status for display (e.g. "pending_approval" -> "Pending Approval").
export function titleizeStatus(s) {
  return String(s || '')
    .replace(/[_-]+/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase())
    .trim() || '—';
}

// Map an inbound message_type to the channel the guest experienced it in, so
// the preview renderer defaults to the truthful format.
export function channelForMessageType(messageType) {
  const t = String(messageType || '').toLowerCase();
  if (t.includes('sms') || t.includes('text')) return 'sms';
  if (t.includes('invite') || t.includes('invitation')) return 'invitation';
  if (t.includes('note') || t.includes('rsvp') || t.includes('website') || t.includes('comment')) return 'note';
  return 'email';
}

export const CHANNEL_LABEL = {
  email: 'Email',
  sms: 'SMS',
  invitation: 'Invitation',
  note: 'Website / RSVP Note',
};

export function guestDisplayName(g) {
  if (!g) return 'Guest';
  const n = `${g.first_name || ''} ${g.last_name || ''}`.trim();
  return n || g.email || g.guest_name || 'Guest';
}

export function pct(value, fallback = '—') {
  if (value === null || value === undefined || Number.isNaN(Number(value))) return fallback;
  return `${Math.round(Number(value) * 100)}%`;
}
